import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";

import {
  AutocompleteMultipleSelect,
  Checkbox,
  Select,
  SelectEvent,
  TextField,
} from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import styles from "../SchoolCandidates.module.scss";
import { searchInstitutionNew } from "../../../api/Institution/InstitutionApi";
import { SearchCard } from "../../../components";
import { useInput, useAuth } from "../../../hooks";
import { CodeBook } from "@king-ict/eupisi-ui/models";

import {
  InstitutionCodeBook,
  SchoolYear,
  InstitutionCodeBookSearch,
  type SaveResult,
} from "../../../models";
import { type SchoolCandidateResidenceChangeSearch } from "../../../models/SchoolCandidate";
import { TableUtils } from "../../../utils";
import type {
  SchoolCandidateBatchDto,
  SchoolCandidateNewResidencesRequest,
} from "../../../queries";
import { DEFAULT_FILTERS } from "../SchoolCandidateAddressAccess/constants";
import { useBatchManagement } from "../hooks";
import { getBooleanFilterValue, getBooleanFromSelectValue } from "./util";
import { YES_NO_OPTIONS } from "./constants";
import { BatchDateSelect, CountyAddressPickupControls, useBatchSync } from ".";

type StoredSchoolCandidateResidenceChangeSearch =
  SchoolCandidateResidenceChangeSearch & {
    batchId?: number | null;
  };

type IProps = {
  authCounties: CodeBook[];
  initialStoredFilters: StoredSchoolCandidateResidenceChangeSearch;
  institutions?: InstitutionCodeBook[];
  schoolYears: SchoolYear[];
  currentSchoolYear: SchoolYear | undefined;
  storageKey: string;
  tablePage: number;
  tablePageSize: number;
  batchManagement: ReturnType<typeof useBatchManagement>;
  countyId: number | undefined;
  onClear: () => void;
  onCountyChange: (countyId?: number) => void;
  onSaveFilters: (filters: SchoolCandidateResidenceChangeSearch) => void;
  onSearchNewResidences: (request: SchoolCandidateNewResidencesRequest) => void;
  onStartAddressPickup: (countyId?: number) => Promise<SaveResult<number>>;
  onSendSelectedSchoolYear: (schoolYearId: number) => void;
};

export const SchoolCandidateAddressAccessFilters = ({
  authCounties,
  initialStoredFilters,
  institutions,
  schoolYears,
  storageKey,
  tablePage,
  tablePageSize,
  batchManagement,
  countyId,
  currentSchoolYear,
  onClear,
  onCountyChange,
  onSaveFilters,
  onSearchNewResidences,
  onStartAddressPickup,
  onSendSelectedSchoolYear,
}: IProps) => {
  const { t } = useTranslation();
  const { handleChange } = useInput();
  const auth = useAuth();

  const [schoolCandidateSearch, setSchoolCandidateSearch] =
    useState<SchoolCandidateResidenceChangeSearch>(initialStoredFilters);
  const [filterChanged, setFilterChanged] = useState(false);
  const [clearFilter, setClearFilter] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [shouldValidateRequired, setShouldValidateRequired] = useState(false);
  const [filteredInstitutions, setFilteredInstitutions] = useState<
    InstitutionCodeBook[]
  >([]);

  const {
    selectedBatchStatus,
    isAddressPickupInProgress,
    selectedBatchId,
    setSelectedBatchId,
  } = batchManagement;

  const storedBatchId = initialStoredFilters.batchId ?? null;
  const { isReadyForInitialSearch, resetBatchSync, markBatchSynced } =
    useBatchSync({
      storedBatchId,
      selectedBatchId,
    });
  const hasRunInitialSearch = useRef(false);

  useEffect(() => {
    resetBatchSync();
  }, [resetBatchSync]);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else {
      setFilterChanged(true);
    }

    TableUtils.RemoveSessionStorageItem(storageKey);
  }, [clearFilter, storageKey]);

  useEffect(() => {
    onSendSelectedSchoolYear(schoolCandidateSearch.schoolYearId);
  }, [schoolCandidateSearch.schoolYearId]);

  const handleSchoolYearChange = (event: ChangeEvent<SelectEvent>) => {
    const schoolYear = Number.parseInt(event.target.value ?? "");
    const newSchoolCandidateSearch: SchoolCandidateResidenceChangeSearch = {
      ...schoolCandidateSearch,
      schoolYearId: schoolYear,
    };

    setSchoolCandidateSearch(newSchoolCandidateSearch);
    setFilterChanged(true);
  };

  const handleInstitutionFilterChange = async (filter: string) => {
    const search: InstitutionCodeBookSearch = {
      onlyActive: true,
      countyIds: countyId ? [countyId] : undefined,
      search: filter,
      includeOutsideEnrollmentArea: false,
      includeHandicapped: false,
      includePrivate: undefined, // both
      includeSatellite: undefined, // both
    };
    const result = await searchInstitutionNew(search);

    setFilteredInstitutions(result ?? []);
  };

  const handleInstitutionIdsChange = (event: ChangeEvent<SelectEvent>) => {
    const selectedInstitutions = event.target.value as InstitutionCodeBook[];
    const institutionIds = selectedInstitutions.length
      ? selectedInstitutions.map((institution) => institution.id)
      : null;

    setSchoolCandidateSearch((prev) => ({
      ...prev,
      institutionIds,
    }));
    setFilterChanged(true);
  };

  const handleUOGUCountyChange = (event: ChangeEvent<SelectEvent>) => {
    const countyId = Number.parseInt(event.target.value ?? "") || undefined;

    const countyInstitutions = institutions?.filter(
      (val) => val.countyId === countyId
    );

    const nextInstitutionIds =
      schoolCandidateSearch.institutionIds?.filter((institutionId) =>
        countyInstitutions?.some(
          (institution) => institution.id === institutionId
        )
      ) ?? [];

    const newSchoolCandidateSearch: SchoolCandidateResidenceChangeSearch = {
      ...schoolCandidateSearch,
      institutionIds: nextInstitutionIds,
    };

    setSchoolCandidateSearch(newSchoolCandidateSearch);
    onCountyChange(countyId);
    setFilterChanged(true);
  };

  const handleBooleanFilterChange =
    (key: "hasRequest" | "addressApproved") =>
    (event: ChangeEvent<SelectEvent>) => {
      const selectedValue = getBooleanFromSelectValue(event.target.value);

      const newSchoolCandidateSearch: SchoolCandidateResidenceChangeSearch = {
        ...schoolCandidateSearch,
        [key]: selectedValue,
      };

      setSchoolCandidateSearch(newSchoolCandidateSearch);
      setFilterChanged(true);
    };

  const handleClearSearchClicked = () => {
    setClearFilter(true);
    const defaultSchoolCandidateResidenceChangeSearch: SchoolCandidateResidenceChangeSearch =
      {
        ...DEFAULT_FILTERS,
        schoolYearId: currentSchoolYear?.id ?? 0,
      };
    setSchoolCandidateSearch(defaultSchoolCandidateResidenceChangeSearch);
    setSearchError("");
    onClear();
  };

  const triggerFilterChange = useCallback(() => {
    setFilterChanged(true);
  }, [setFilterChanged]);

  const isSearchDisabled =
    !schoolCandidateSearch.schoolYearId ||
    !countyId ||
    !selectedBatchId ||
    !schoolCandidateSearch.processedAt;

  const handleSearchClicked = useCallback(() => {
    setFilterChanged(false);

    setShouldValidateRequired(true);

    if (isSearchDisabled) {
      setSearchError("Molimo popunite sva obavezna polja.");
      return;
    }

    setSearchError("");
    setShouldValidateRequired(false);

    const searchRequest: SchoolCandidateResidenceChangeSearch = {
      ...schoolCandidateSearch,
      processedAt:
        schoolCandidateSearch.processedAt ??
        selectedBatchStatus?.processedAt ??
        null,
      page: tablePage,
      pageSize: tablePageSize,
    };

    const request: SchoolCandidateNewResidencesRequest = {
      countyId,
      batchId: selectedBatchId,
      search: searchRequest,
    };

    onSearchNewResidences(request);
    onSaveFilters(searchRequest);
  }, [
    selectedBatchId,
    countyId,
    onSearchNewResidences,
    onSaveFilters,
    schoolCandidateSearch,
    selectedBatchStatus?.processedAt,
    tablePage,
    tablePageSize,
  ]);

  // Triggered only on table state change
  useEffect(() => {
    if (!hasRunInitialSearch.current) return;

    handleSearchClicked();
  }, [tablePage, tablePageSize]);

  useEffect(() => {
    if (
      isSearchDisabled ||
      !isReadyForInitialSearch ||
      hasRunInitialSearch.current
    ) {
      return;
    }

    hasRunInitialSearch.current = true;
    handleSearchClicked();
  }, [isSearchDisabled, isReadyForInitialSearch, handleSearchClicked]);

  const handleBatchDateChange = useCallback(
    (batch: SchoolCandidateBatchDto | null) => {
      setSchoolCandidateSearch((prev) => ({
        ...prev,
        processedAt: batch?.processedAt ?? null,
        schoolYearId: batch?.schoolYearId ?? prev.schoolYearId,
      }));

      setSelectedBatchId(batch?.id ?? null);

      markBatchSynced(batch?.id ?? null);

      setFilterChanged(true);
    },
    [markBatchSynced, setSelectedBatchId]
  );

  return (
    <SearchCard
      onClickSearch={handleSearchClicked}
      onClickClearSearch={handleClearSearchClicked}
      isFilterChanged={filterChanged}
      textError={searchError}
      hideDivider={true}
    >
      <Row>
        {/* First main column */}
        <Col md={6}>
          <CountyAddressPickupControls
            authCounties={authCounties}
            countyId={countyId}
            shouldValidateRequired={shouldValidateRequired}
            isAddressPickupInProgress={isAddressPickupInProgress}
            currentRole={auth.currentRole?.role}
            onCountyChange={handleUOGUCountyChange}
            onStartAddressPickup={onStartAddressPickup}
          />
          <Row className={styles.rowPaddingSmall}>
            <Col md={4}>
              <TextField
                label={t("common.schoolCandidateModel.lastName")}
                name={nameof<typeof schoolCandidateSearch>("lastName")}
                value={schoolCandidateSearch.lastName ?? ""}
                onChange={handleChange(
                  setSchoolCandidateSearch,
                  triggerFilterChange
                )}
              />
            </Col>
            <Col md={4}>
              <TextField
                label={t("common.schoolCandidateModel.firstName")}
                name={nameof<typeof schoolCandidateSearch>("firstName")}
                value={schoolCandidateSearch.firstName ?? ""}
                onChange={handleChange(
                  setSchoolCandidateSearch,
                  triggerFilterChange
                )}
              />
            </Col>
            <Col md={4}>
              <TextField
                label={t("common.schoolCandidateModel.oib")}
                name={nameof<typeof schoolCandidateSearch>("oib")}
                value={schoolCandidateSearch.oib ?? ""}
                onChange={handleChange(
                  setSchoolCandidateSearch,
                  triggerFilterChange
                )}
              />
            </Col>
          </Row>
          <AutocompleteMultipleSelect<InstitutionCodeBook>
            label={t("common.schoolCandidateModel.institutionList")}
            name={nameof<typeof schoolCandidateSearch>("institutionIds")}
            value={
              institutions?.filter((v) =>
                schoolCandidateSearch?.institutionIds?.some(
                  (instId) => instId === v.id
                )
              ) ?? []
            }
            valueProp="id"
            textProp="name"
            onChange={handleInstitutionIdsChange}
            selectOptions={filteredInstitutions}
            onFilterChange={handleInstitutionFilterChange}
            maximumSelectedItems={10}
          />
          <Row className={`${styles.rowPaddingMedium}`}>
            <Col md={6}>
              <Checkbox
                label="Nerasporedeni školski obveznici"
                name={nameof<typeof schoolCandidateSearch>(
                  "unassignedSchoolCandidates"
                )}
                checked={schoolCandidateSearch.unassignedSchoolCandidates}
                onChange={handleChange(
                  setSchoolCandidateSearch,
                  triggerFilterChange
                )}
              />
            </Col>
            <Col md={6}>
            <Checkbox
              label="Dodani školski obveznici"
              name={nameof<typeof schoolCandidateSearch>(
                "addedSchoolCandidates"
              )}
              checked={schoolCandidateSearch.addedSchoolCandidates}
              onChange={handleChange(
                setSchoolCandidateSearch,
                triggerFilterChange
              )}
            />
          </Col>
            <Col md={6}>
              <Checkbox
                label="Adresa je izvan nadležne županije"
                name={nameof<typeof schoolCandidateSearch>(
                  "newResidenceInDifferentCounty"
                )}
                checked={schoolCandidateSearch.newResidenceInDifferentCounty}
                onChange={handleChange(
                  setSchoolCandidateSearch,
                  triggerFilterChange
                )}
              />
            </Col>
            <Col md={6}>
              <Checkbox
                label="Adresa je unutar nadležne županije"
                name={nameof<typeof schoolCandidateSearch>(
                  "newResidenceInSameCounty"
                )}
                checked={schoolCandidateSearch.newResidenceInSameCounty}
                onChange={handleChange(
                  setSchoolCandidateSearch,
                  triggerFilterChange
                )}
              />
            </Col>
          </Row>
          <Row className={styles.rowPaddingMedium}>
            {/* Temporarily hidden, will be-re added in the future */}
            {/* <Col md={6}>
              <Checkbox
                label="Odgoda od prethodne godine"
                name={nameof<typeof schoolCandidateSearch>("delayPreviousYear")}
                checked={schoolCandidateSearch.delayPreviousYear}
                onChange={handleChange(
                  setSchoolCandidateSearch,
                  triggerFilterChange
                )}
              />
            </Col> */}
            {/* <Col md={6}>
              <Checkbox
                label="Djeca koja nisu kandidati za upis"
                name={nameof<typeof schoolCandidateSearch>(
                  "notCandidatesForEnrollment"
                )}
                checked={schoolCandidateSearch.notCandidatesForEnrollment}
                onChange={handleChange(
                  setSchoolCandidateSearch,
                  triggerFilterChange
                )}
              />
            </Col> */}
          </Row>
        </Col>
        {/* Second main column */}
        <Col md={6}>
          <Select
            label={t("common.schoolCandidateModel.schoolYearId")}
            name={nameof<typeof schoolCandidateSearch>("schoolYearId")}
            value={schoolCandidateSearch.schoolYearId}
            required
            onChange={handleSchoolYearChange}
            selectData={codeBookToSelectData(schoolYears)}
            errorText={
              shouldValidateRequired && !schoolCandidateSearch.schoolYearId
                ? t("validations.required")
                : undefined
            }
          />

          <BatchDateSelect
            countyId={countyId}
            schoolYearId={schoolCandidateSearch.schoolYearId}
            batchId={selectedBatchId}
            initialBatchStatus={batchManagement.selectedBatchStatus}
            onChange={handleBatchDateChange}
            errorText={
              shouldValidateRequired &&
              (!selectedBatchId || !schoolCandidateSearch.processedAt)
                ? t("validations.required")
                : undefined
            }
          />
          <Select
            label="Predan zahtjev"
            name={nameof<typeof schoolCandidateSearch>("hasRequest")}
            value={getBooleanFilterValue(schoolCandidateSearch.hasRequest)}
            onChange={handleBooleanFilterChange("hasRequest")}
            selectData={YES_NO_OPTIONS}
            emptyOption={{ value: undefined, text: t("common.all") }}
          />
          <Select
            label="Adresa preuzeta"
            name={nameof<typeof schoolCandidateSearch>("addressApproved")}
            value={getBooleanFilterValue(schoolCandidateSearch.addressApproved)}
            onChange={handleBooleanFilterChange("addressApproved")}
            selectData={YES_NO_OPTIONS}
            emptyOption={{ value: undefined, text: t("common.all") }}
          />
        </Col>
      </Row>
    </SearchCard>
  );
};
